/**
 * Per-route SEO metadata.
 * ---------------------------------------------------------------------------
 * Source of truth for every route's title, description, and indexability.
 * Consumed by <Seo /> (src/components/Seo.jsx).
 *
 * This is a SEPARATE file from src/seo/keywords.js on purpose. keywords.js is a
 * body-copy phrase bank that other contributors edit; keeping metadata here
 * avoids merge conflicts with them.
 *
 * RULES (enforced by validatePageMeta below, which warns in development):
 *   - title       <= 60 characters, unique, brand name in every one
 *   - description <= 155 characters, unique
 *   - Non-essential pages (about, team, contact, terms, privacy) carry NO
 *     keywords in their titles — they must not compete with the product pages.
 *
 * When you add a route to src/App.js, add it here AND to public/sitemap.xml.
 */

/** Canonical host. Non-www redirects here — never emit the non-www form. */
export const SITE_URL = "https://www.takamorirobot.com";

/** Default share image. 789x525. Used unless a route overrides it. */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/kickstarterRobotic/kickstarterFirstgen.webp`;
export const DEFAULT_OG_IMAGE_ALT =
  "The H.I.R.O. TR-01-A robotics engineering kit by Takamori Robot";

export const BRAND = "Takamori Robot";

/**
 * Fallback metadata for any path with no PAGE_META entry.
 *
 * These MUST exist. src/index.js strips the fallback tags out of index.html so
 * there is never a duplicate <title>, which means <Seo /> is the only source of
 * a title once JS runs. Rendering nothing here leaves the browser tab blank and
 * makes useAnalytics report an empty page_title.
 */
export const DEFAULT_TITLE = "Takamori Robot — Hands-On Robotics Engineering Kits";
export const DEFAULT_DESCRIPTION =
  "Hands-on robotics engineering kits for students, classrooms, and makers. Build, program, and iterate with the H.I.R.O. TR-01-A kit from Takamori Robot.";

/**
 * Normalise a pathname before looking it up in PAGE_META.
 *
 * react-router matches routes case-insensitively and tolerates a trailing
 * slash, so `/About`, `/about/`, and `/about` all render the About page. Neither
 * deploy path redirects between them (vercel.json is a bare catch-all rewrite,
 * spa-nginx.conf is try_files). Without normalising:
 *   - the lookup misses, so those URLs get no title at all
 *   - the canonical would differ per variant, splitting one page across URLs
 */
export function normalizeRoute(pathname) {
  if (!pathname) return "/";
  let p = pathname.toLowerCase();
  // collapse duplicate slashes, then drop a single trailing slash (not on root)
  p = p.replace(/\/{2,}/g, "/");
  if (p.length > 1) p = p.replace(/\/+$/, "");
  return p === "" ? "/" : p;
}

const KICKSTARTER_URL =
  "https://www.kickstarter.com/projects/lightning-alchemist/hiro-the-new-standard-in-robotics-platforms";

/**
 * Route-specific JSON-LD.
 * ---------------------------------------------------------------------------
 * The sitewide Organization schema lives in public/index.html (it must be
 * JS-free so non-JS crawlers can read it). These are the page-level additions,
 * rendered by <Seo />.
 *
 * !! KEEP IN SYNC WITH THE PAGE !!
 * Google requires structured data to match content visible on the page. The
 * prices and product names below are duplicated from:
 *   /roboticskits -> src/pages/shop/shop.js        (items array)
 *   /robotickits  -> src/pages/robotic_kit/roboticKit.js (items array)
 * If you change a price or product name in either file, change it here too or
 * the page will report a structured-data mismatch in Search Console.
 *
 * Deliberate choices:
 *   - Only /roboticskits declares Product + Offer, because that is the page
 *     whose links actually lead to a purchase (the Shopify storefront).
 *   - /robotickits uses ItemList instead, because its links go to Kickstarter,
 *     which is not a retail purchase URL. This also avoids declaring two
 *     competing Product entities for the same kit on two different URLs.
 *   - No aggregateRating anywhere: only add one with 5+ verifiable reviews.
 */
const ORG_REF = { "@id": `${SITE_URL}/#organization` };

export const PAGE_JSON_LD = {
  "/roboticskits": [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "@id": `${SITE_URL}/roboticskits#standard-edition`,
      name: "H.I.R.O. TR-01-A Standard Edition Robotics Kit",
      description:
        "Beginner-friendly robotics engineering kit. The entry point for students and first-time builders.",
      image: `${SITE_URL}/images/shop/standard_kit.png`,
      brand: { "@type": "Brand", name: BRAND },
      manufacturer: ORG_REF,
      offers: {
        "@type": "Offer",
        url: "https://shop.takamorirobot.com/products/h-i-r-o-tr-01-a-standard-edition-pre-order-here",
        price: "114.99",
        priceCurrency: "USD",
        availability: "https://schema.org/PreOrder",
        seller: ORG_REF,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "@id": `${SITE_URL}/roboticskits#trailblazer-edition`,
      name: "H.I.R.O. Robotics Kit Trailblazer Edition",
      description:
        "Intermediate to advanced robotics engineering kit. Limited edition with early access and additional included merchandise.",
      image: `${SITE_URL}/images/shop/trailblazer_kit.png`,
      brand: { "@type": "Brand", name: BRAND },
      manufacturer: ORG_REF,
      offers: {
        "@type": "Offer",
        url: "https://shop.takamorirobot.com/products/h-i-r-o-robotics-kit-trailblazers-edition",
        price: "249.99",
        priceCurrency: "USD",
        availability: "https://schema.org/PreOrder",
        seller: ORG_REF,
      },
    },
  ],

  "/robotickits": [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${SITE_URL}/robotickits#kit-list`,
      name: "H.I.R.O. Model TR-01-A robotics kits",
      description:
        "Editions of the H.I.R.O. Model TR-01-A robotics engineering kit, built on the Raspberry Pi Pico W with 3D printed PLA parts.",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "H.I.R.O. Model TR-01-A Kit",
          url: KICKSTARTER_URL,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Zero to H.I.R.O. Exploration Kit",
          url: KICKSTARTER_URL,
        },
      ],
    },
  ],
};

/**
 * Keyed by the exact `path` in src/App.js.
 * `noindex: true` keeps a page out of search results but still lets crawlers
 * follow its links. Those routes are also excluded from public/sitemap.xml.
 */
export const PAGE_META = {
  "/": {
    title: "Takamori Robot — Hands-On Robotics Engineering Kits",
    description:
      "Hands-on robotics engineering kits for students, classrooms, and makers. Build, program, and iterate with the H.I.R.O. TR-01-A kit from Takamori Robot.",
  },

  // --- Product pages: primary commercial intent, keywords expected ---
  "/robotickits": {
    title: "H.I.R.O. Robotics Kits | Takamori Robot",
    description:
      "Explore the H.I.R.O. TR-01-A robotics engineering kit: modular hardware you can build, program, and extend. Designed for beginners through advanced makers.",
  },
  "/roboticskits": {
    title: "Shop Robotics Kits | Takamori Robot",
    description:
      "Compare H.I.R.O. TR-01-A editions. Standard Edition for beginners and Trailblazer Edition for intermediate to advanced builders. Order from our shop.",
  },

  // --- Community: real content ---
  "/community": {
    title: "Community | Takamori Robot",
    description:
      "Join the Takamori Robot community on Discord, YouTube, GitHub, Reddit, Instagram, LinkedIn, and Facebook. See what people are building with H.I.R.O.",
  },

  /*
   * PLACEHOLDER PAGES — noindex until they have real content.
   * ---------------------------------------------------------------------
   * These three render only "<Page> page is under construction." Indexing a
   * thin page lowers the whole site's quality score, which drags down the
   * pages that are good. They are also excluded from public/sitemap.xml.
   *
   * WHEN YOU BUILD THESE OUT: remove `noindex` here, write a real description,
   * and add the route back to public/sitemap.xml in the same commit.
   */
  "/education": {
    title: "For Education | Takamori Robot",
    description:
      "Classroom and educator programs from Takamori Robot. This page is in progress.",
    noindex: true,
  },
  "/classes": {
    title: "Classes | Takamori Robot",
    description:
      "Robotics classes from Takamori Robot. This page is in progress.",
    noindex: true,
  },
  "/gallery": {
    title: "Gallery | Takamori Robot",
    description:
      "Photos of H.I.R.O. builds and events from Takamori Robot. This page is in progress.",
    noindex: true,
  },

  /*
   * REDIRECT ROUTE — noindex. tutorials.jsx forwards to the YouTube playlist
   * after ~1.5s; it is not a page. Never index a redirect (SEO spec 6.4).
   * If real on-site tutorial content is ever written, remove `noindex` and add
   * the route to public/sitemap.xml.
   */
  "/tutorials": {
    title: "Tutorials | Takamori Robot",
    description:
      "Redirecting to the Takamori Robot tutorial playlist on YouTube.",
    noindex: true,
  },

  // --- Non-essential pages: NO keywords in titles (SEO spec 3.2) ---
  "/about": {
    title: "About Us | Takamori Robot",
    description:
      "Takamori Robot is a small hardware startup in Tacoma, Washington. Learn where we came from, what drives us, and why we build the kits we build.",
  },
  "/team": {
    title: "Our Team | Takamori Robot",
    description:
      "Meet the people behind Takamori Robot — the engineers, designers, and educators building the H.I.R.O. robotics platform in Tacoma, Washington.",
  },
  "/subscribe": {
    title: "Stay Updated | Takamori Robot",
    description:
      "Sign up for Takamori Robot updates on new products, launches, events, and company news. No spam, and you can unsubscribe at any time.",
  },
  "/contact-us": {
    title: "Contact Us | Takamori Robot",
    description:
      "Get in touch with Takamori Robot about kits, classroom programs, bulk orders, partnerships, or support. We read and answer every message.",
  },

  // --- Legal: indexable pages add nothing, so keep them out of results ---
  "/terms": {
    title: "Terms of Service | Takamori Robot",
    description:
      "The terms governing your use of the Takamori Robot website, products, and services.",
    noindex: true,
  },
  "/privacy-policy": {
    title: "Privacy Policy | Takamori Robot",
    description:
      "How Takamori Robot collects, uses, stores, and protects your personal information.",
    noindex: true,
  },

  /*
   * REDIRECT ROUTE — noindex. ShopRedirect.jsx forwards to the Shopify
   * storefront. It is declared in src/App.js, so it needs an entry here or its
   * visible "Redirecting to Takamori Shop..." page would have a blank tab title.
   */
  "/shop": {
    title: "Shop | Takamori Robot",
    description: "Redirecting to the Takamori Robot shop.",
    noindex: true,
  },
};

export const TITLE_MAX = 60;
export const DESCRIPTION_MAX = 155;

/**
 * Lightweight stand-in for the build-time SEO validation in the bb-codex spec
 * (section 10.1). CRA has no build hook to fail on, so this warns loudly in
 * development instead. Called once by <Seo /> in non-production builds.
 */
export function validatePageMeta(meta = PAGE_META) {
  const problems = [];
  const seenTitles = new Map();
  const seenDescriptions = new Map();

  for (const [path, entry] of Object.entries(meta)) {
    if (!entry.title) problems.push(`${path}: missing title`);
    if (!entry.description) problems.push(`${path}: missing description`);

    if (entry.title) {
      if (entry.title.length > TITLE_MAX) {
        problems.push(
          `${path}: title is ${entry.title.length} chars (max ${TITLE_MAX})`
        );
      }
      if (!entry.title.includes(BRAND)) {
        problems.push(`${path}: title is missing the brand name "${BRAND}"`);
      }
      if (seenTitles.has(entry.title)) {
        problems.push(
          `${path}: duplicate title, already used by ${seenTitles.get(entry.title)}`
        );
      } else {
        seenTitles.set(entry.title, path);
      }
    }

    if (entry.description) {
      if (entry.description.length > DESCRIPTION_MAX) {
        problems.push(
          `${path}: description is ${entry.description.length} chars (max ${DESCRIPTION_MAX})`
        );
      }
      if (seenDescriptions.has(entry.description)) {
        problems.push(
          `${path}: duplicate description, already used by ${seenDescriptions.get(
            entry.description
          )}`
        );
      } else {
        seenDescriptions.set(entry.description, path);
      }
    }
  }

  return problems;
}
